import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { AuthContextProvider, AuthContext } from '../../context/AuthContext';
import Login from '../Login/Login';
import LoginBlack from '../LoginBlack/LoginBlack';
import Dashboard from '../Dashboard/Dashboard';
import Welcome from '../Welcome/Welcome';
import WelcomeBlack from '../WelcomeBlack/WelcomeBlack';
import Dispositivos from '../Dispositivos/Dispositivos';
import AgregarDispositivo from '../Dispositivos/AgregarDispositivo';
import EditarDispositivoSimple from '../Dispositivos/EditarDispositivoSimple';
import MapaCompleto from '../Mapa/MapaCompleto';
import MapaBlack from '../MapaBlack/MapaBlack';
import MapaBlackSimple from '../MapaBlack/MapaBlackSimple';
import { ListaSentinels } from '../Sentinels';
import { Tags } from '../Tags';
import { DispositivosSsem, CrearDispositivo, DetalleDispositivo, EditarDispositivo } from '../DispositivosSsem';
import { ProductosDinamicos, VariablesProductos } from '../ProductosDinamicos';
import ListaUsuarios from '../Usuarios/ListaUsuarios';
import CrearUsuario from '../Usuarios/CrearUsuario';
import EditarUsuario from '../Usuarios/EditarUsuario';
import DetalleUsuario from '../Usuarios/DetalleUsuario';
import Subcuentas from '../Subcuentas/Subcuentas';
import Roles from '../Roles/Roles';
import Permisos from '../Permisos/Permisos';
import { Wrapper } from '../../components/Wrapper/Wrapper';
import { WrapperBlack } from '../../components/WrapperBlack/WrapperBlack';
import 'react-toastify/dist/ReactToastify.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent: React.FC = () => {
  const authContext = useContext(AuthContext);

  return (
    <div className="App">
      <Routes>
        {(!authContext.isLoggedIn || authContext.rol === null) && (
          <>
            <Route path="/" element={<Navigate to="/loginca" replace />} />
            <Route path="/loginca" element={<LoginBlack />} />
            <Route path="/loginca" element={<LoginBlack />} />
            <Route path="*" element={<Navigate to="/loginca" replace />} />
          </>
        )}
        {authContext.isLoggedIn && (
          <>
            {/* Rutas para plataforma Samsung */}
            {authContext.plataforma?.slug === 'samsung' && (
              <>
                <Route path="*" element={<Navigate to="/welcome" />} />
                <Route path="/" element={<Navigate to="/welcome" />} />
                <Route path="/welcome" element={<Wrapper />}>
                  <Route index element={<Welcome />} />
                </Route>
                <Route path="/dashboard" element={<Wrapper />}>
                  <Route index element={<Dashboard />} />
                </Route>
                <Route path="/dispositivos" element={<Wrapper />}>
                  <Route index element={<Dispositivos />} />
                </Route>
                <Route path="/dispositivos/agregar" element={<Wrapper />}>
                  <Route index element={<AgregarDispositivo />} />
                </Route>
                <Route path="/dispositivos/editar/:id" element={<Wrapper />}>
                  <Route index element={<EditarDispositivoSimple />} />
                </Route>
                <Route path="/sentinels" element={<Wrapper />}>
                  <Route index element={<ListaSentinels />} />
                </Route>
                <Route path="/mapa" element={<Wrapper />}>
                  <Route index element={<MapaCompleto token={authContext.token || ''} />} />
                </Route>
              </>
            )}

            {/* Rutas para plataforma Sky Angel (Black) - Todas las plataformas excepto Samsung */}
            {authContext.plataforma?.slug && authContext.plataforma?.slug !== 'samsung' && (
              <>
                <Route path="*" element={<Navigate to="/welcome-ca" />} />
                <Route path="/" element={<Navigate to="/welcome-ca" />} />
                <Route path="/welcome-ca" element={<WelcomeBlack />} />

                {authContext.tienePermiso('tags.ver') && (
                  <Route path="/tags" element={<WrapperBlack />}>
                    <Route index element={<Tags />} />
                  </Route>
                )}

                {authContext.tienePermiso('dispositivos.ver') && (
                  <>
                    <Route path="/dispositivos-ssem" element={<WrapperBlack />}>
                      <Route index element={<DispositivosSsem />} />
                    </Route>
                    <Route path="/dispositivos-ssem/:id" element={<WrapperBlack />}>
                      <Route index element={<DetalleDispositivo />} />
                    </Route>
                  </>
                )}

                {authContext.tienePermiso('dispositivos.gestionar') && (
                  <>
                    <Route path="/dispositivos-ssem/crear" element={<WrapperBlack />}>
                      <Route index element={<CrearDispositivo />} />
                    </Route>
                    <Route path="/dispositivos-ssem/:id/editar" element={<WrapperBlack />}>
                      <Route index element={<EditarDispositivo />} />
                    </Route>
                  </>
                )}

                {authContext.tienePermiso('productos.ver') && (
                  <Route path="/productos-dinamicos" element={<WrapperBlack />}>
                    <Route index element={<ProductosDinamicos />} />
                  </Route>
                )}

                {authContext.tienePermiso('variables.ver') && (
                  <Route path="/variables-productos" element={<WrapperBlack />}>
                    <Route index element={<VariablesProductos />} />
                  </Route>
                )}

                {/* Rutas de Usuarios */}
                {authContext.tienePermiso('usuarios.ver') && (
                  <>
                    <Route path="/usuarios" element={<WrapperBlack />}>
                      <Route index element={<ListaUsuarios />} />
                    </Route>
                    <Route path="/usuarios/crear" element={<WrapperBlack />}>
                      <Route index element={<CrearUsuario />} />
                    </Route>
                    <Route path="/usuarios/:id" element={<WrapperBlack />}>
                      <Route index element={<DetalleUsuario />} />
                    </Route>
                    <Route path="/usuarios/:id/editar" element={<WrapperBlack />}>
                      <Route index element={<EditarUsuario />} />
                    </Route>
                  </>
                )}

                {/* Subcuentas */}
                {authContext.tienePermiso('subcuentas.ver') && (
                  <Route path="/subcuentas" element={<WrapperBlack />}>
                    <Route index element={<Subcuentas />} />
                  </Route>
                )}

                {/* Roles y Permisos */}
                {authContext.tienePermiso('roles.ver') && (
                  <Route path="/roles" element={<WrapperBlack />}>
                    <Route index element={<Roles />} />
                  </Route>
                )}
                {authContext.tienePermiso('permisos.ver') && (
                  <Route path="/permisos" element={<WrapperBlack />}>
                    <Route index element={<Permisos />} />
                  </Route>
                )}

                {authContext.tienePermiso('mapa.ver') && (
                  <Route path="/mapa" element={<WrapperBlack />}>
                    <Route index element={<MapaBlack />} />
                  </Route>
                )}
              </>
            )}

            {/* Fallback si no hay plataforma definida */}
            {!authContext.plataforma && (
              <>
                <Route path="*" element={<Navigate to="/welcome" />} />
                <Route path="/welcome" element={<Wrapper />}>
                  <Route index element={<Welcome />} />
                </Route>
              </>
            )}
          </>
        )}
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthContextProvider>
        <AppContent />
      </AuthContextProvider>
    </QueryClientProvider>
  );
};

export default App;
